import { liste as angebotsliste, STATUSNAME as ANGEBOTSSTATUS, type Angebotszeile } from '../api/angebote';
import { liste as auftragsliste, STATUSNAME as AUFTRAGSSTATUS, type Auftragszeile } from '../api/auftraege';
import { suchen as buchungssuche, type Buchungstreffer } from '../api/buchungen';
import { liste as rahmenliste, type Rahmenzeile } from '../api/rahmenauftraege';
import {
  liste as rechnungsliste,
  mahnungen as mahnungsliste,
  offenePosten as offenePostenListe,
  ZAHLUNGSSTANDNAME,
  type Navisionmahnung,
  type OffenerPosten,
  type Rechnungszeile,
} from '../api/rechnungen';
import { kommunikation as kommunikationsliste, STATUSNAME as VORGANGSSTATUS, type Vorgangszeile } from '../api/support';
import { alsEuro } from '../betrag';
import type { Verlaufseintrag } from '../komponenten/Akte';

/**
 * ============================================================================
 * WAS DAS CRM ÜBER EINEN KUNDEN SCHON WEISS (KUN-14, ÄA-41)
 * ============================================================================
 *
 * KUN-14 verlangt „eine chronologische Aktivitätshistorie je Kunde: Notizen,
 * Telefonate, Termine, versendete Belege, Statuswechsel und Systemereignisse
 * in einem Strang". Der Strang als eigenes Modul — mit Notizen und
 * Telefonaten — ist nicht gebaut (offener Punkt in ÄA-41).
 *
 * Was es gibt, sind die Vorgänge selbst: Angebote, Aufträge, Rechnungen,
 * offene Posten, Rahmenaufträge und Buchungen, jeder mit seinem Datum.
 * Wartelisteneinträge stehen seit ÄA-55 je Training und E-Mail-Adresse, nicht
 * am Kunden. Die Seitenleiste der Kundenakte zeigt SIE in einem Strang, so
 * wie der Entwurf es für Aktivitäten tut — nichts wird dazu erfunden.
 *
 * JEDE QUELLE DARF FEHLEN. Wer keine Rechnungen sehen darf, bekommt eine
 * Absage vom Server (BEN-08); die Akte zeigt dann den Rest und nicht eine rote
 * Fläche. `undefined` heißt: nicht lesbar — und ist etwas anderes als leer.
 */
export type Kundenvorgaenge = {
  readonly angebote: readonly Angebotszeile[] | undefined;
  readonly auftraege: readonly Auftragszeile[] | undefined;
  readonly rechnungen: readonly Rechnungszeile[] | undefined;
  readonly offenePosten: readonly OffenerPosten[] | undefined;
  readonly rahmen: readonly Rahmenzeile[] | undefined;
  readonly buchungen: readonly Buchungstreffer[] | undefined;
  /** RNG-25: die Mahnungen aus Navision, auch zu schon bezahlten Rechnungen. */
  readonly mahnungen?: readonly Navisionmahnung[] | undefined;
  /** SUP-11: die E-Mail-Vorgänge aus den Postfächern, nur mit support/sehen. */
  readonly kommunikation?: readonly Vorgangszeile[] | undefined;
};

async function oderNichts<T>(abfrage: () => Promise<T>): Promise<T | undefined> {
  try {
    return await abfrage();
  } catch {
    return undefined;
  }
}

/**
 * Alle Vorgänge eines Kunden, nebeneinander geholt.
 *
 * DIE BUCHUNGEN kommen über die Buchungssuche (BUC-80) mit der
 * Kundennummer: Sie findet den Kunden über `fach.kunde_suchen` und dazu
 * Buchungen, in denen ein Teilnehmer ähnlich heißt. Übernommen werden nur die
 * Treffer mit genau dieser Kundennummer — eine eigene Abfrage je Kunde gibt es
 * nicht, und die Suche liefert höchstens ihre Obergrenze.
 */
export async function ladeVorgaenge(kunde: {
  readonly id: string;
  readonly kundennummer: string;
}): Promise<Kundenvorgaenge> {
  const [angebote, auftraege, rechnungen, offenePosten, rahmen, buchungen, mahnungen, vorgaenge] =
    await Promise.all([
      oderNichts(() => angebotsliste({ kunde: kunde.id })),
      oderNichts(() => auftragsliste({ kunde: kunde.id })),
      oderNichts(() => rechnungsliste({ kunde: kunde.id })),
      oderNichts(() => offenePostenListe({ kunde: kunde.id })),
      oderNichts(() => rahmenliste({ kunde: kunde.id })),
      oderNichts(async () =>
        (await buchungssuche({ name: kunde.kundennummer })).treffer.filter(
          (t) => t.kundennummer === kunde.kundennummer,
        ),
      ),
      oderNichts(() => mahnungsliste({ kunde: kunde.id })),
      oderNichts(() => kommunikationsliste(kunde.id)),
    ]);
  return {
    angebote, auftraege, rechnungen, offenePosten, rahmen, buchungen, mahnungen,
    kommunikation: vorgaenge?.vorgaenge,
  };
}

/** Das Datum als Kalendertag in Ortszeit (FRS-01), `JJJJ-MM-TT`. */
function tag(iso: string): string {
  return new Date(iso).toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
}

/** Kurz wie im Entwurf: „28.08.". */
export function kurzTag(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'Europe/Berlin',
  });
}

/** Lang: „28.08.2026". */
export function langTag(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  });
}

/** Wohin ein Eintrag führt — die Akte übersetzt es in eine Maske. */
export type Sprungziele = {
  readonly angebot?: (id: string) => void;
  readonly auftrag?: (id: string) => void;
  readonly rechnung?: (id: string) => void;
  readonly buchung?: (nummer: string) => void;
  /** SUP-11: der Vorgang im Posteingang seines Postfachs. */
  readonly vorgang?: (id: string, postfach: string) => void;
};

type Datiert = Verlaufseintrag & { readonly wann: string };

/**
 * NÄCHSTE SCHRITTE: was ab heute eine Frist hat.
 *
 * Laufende Angebote bis zu ihrem Gültigkeitsende, offene Posten bis zur
 * Fälligkeit (überfällige in Rost), kommende Buchungen am Termin. Das früheste zuerst — wie im Entwurf.
 */
/**
 * RNG-24, BP-31 (Auftrag CRM-113): der Saldo der offenen Posten eines Kunden,
 * Forderungen positiv, Guthaben negativ. In Hundertsteln gerechnet — der
 * Server liefert zwei Nachkommastellen, eine Summe in Gleitkomma ginge um
 * einen Cent daneben.
 */
/** RNG-25: die höchste Mahnstufe unter den offenen Posten; null heißt ungemahnt. */
export function hoechsteMahnstufe(posten: readonly OffenerPosten[]): number | null {
  const stufen = posten.map((p) => p.mahnstufe).filter((m): m is number => m != null);
  return stufen.length === 0 ? null : Math.max(...stufen);
}

export function saldoDerPosten(posten: readonly OffenerPosten[]): string {
  let cent = 0n;
  for (const p of posten) {
    const [ganz = '0', bruch = ''] = p.offenerBetrag.replace('-', '').split('.');
    const wert = BigInt(ganz) * 100n + BigInt((bruch + '00').slice(0, 2));
    cent += p.offenerBetrag.startsWith('-') ? -wert : wert;
  }
  const ohne = cent < 0n ? -cent : cent;
  return `${cent < 0n ? '-' : ''}${String(ohne / 100n)}.${String(ohne % 100n).padStart(2, '0')}`;
}

export function naechsteSchritte(
  v: Kundenvorgaenge,
  heute: string,
  ziele: Sprungziele,
): readonly Verlaufseintrag[] {
  const schritte: Datiert[] = [];

  for (const a of v.angebote ?? []) {
    if ((a.status === 'versendet' || a.status === 'verhandlung') && a.gueltigBis >= heute) {
      schritte.push({
        kennung: `angebot-${a.id}`,
        art: 'vorgang',
        sinnbild: 'check-square',
        titel: `Angebot ${a.angebotsnummer} nachfassen`,
        detail: `${a.betreff} · gültig bis ${langTag(a.gueltigBis)}`,
        datum: kurzTag(a.gueltigBis),
        wann: a.gueltigBis,
        beiWahl: ziele.angebot === undefined ? undefined : () => ziele.angebot?.(a.id),
      });
    }
  }

  for (const p of v.offenePosten ?? []) {
    /* BP-31, DAT-12: ein Guthaben nach Storno oder Gutschrift wartet auf Erstattung oder Verrechnung. */
    if (p.art === 'guthaben') {
      schritte.push({
        kennung: `posten-${p.rechnung}`,
        art: 'achtung',
        sinnbild: 'currency-eur',
        titel: `Guthaben aus ${p.rechnungsnummer} erstatten oder verrechnen`,
        detail: `${alsEuro(p.offenerBetrag.replace('-', ''))}${
          p.ausgleich === null ? '' : ` · ausgeglichen durch ${p.ausgleich}`}`,
        dringend: true,
        datum: kurzTag(heute),
        wann: heute,
        beiWahl: ziele.rechnung === undefined ? undefined : () => ziele.rechnung?.(p.rechnung),
      });
      continue;
    }
    const ueber = p.tageUeberfaellig > 0;
    schritte.push({
      kennung: `posten-${p.rechnung}`,
      art: ueber ? 'achtung' : 'vorgang',
      sinnbild: ueber ? 'warning-circle' : 'receipt',
      titel: `Rechnung ${p.rechnungsnummer} ${ueber ? 'überfällig' : 'fällig'}`,
      detail: ueber
        ? `seit ${p.tageUeberfaellig} ${p.tageUeberfaellig === 1 ? 'Tag' : 'Tagen'} · ${p.staffel}${
          /* RNG-25: die letzte Mahnstufe aus Navision. */
          p.mahnstufe == null ? '' : ` · ${p.mahnstufe}. Mahnung`}`
        : `fällig am ${langTag(p.faelligAm)}`,
      dringend: ueber,
      datum: kurzTag(p.faelligAm),
      wann: p.faelligAm,
      beiWahl: ziele.rechnung === undefined ? undefined : () => ziele.rechnung?.(p.rechnung),
    });
  }

  for (const b of v.buchungen ?? []) {
    if (tag(b.beginn) >= heute && b.status !== 'storniert' && b.status !== 'abgesagt') {
      schritte.push({
        kennung: `buchung-${b.id}`,
        art: 'termin',
        sinnbild: 'calendar-blank',
        titel: b.produkt,
        detail: `${b.plaetze} ${b.plaetze === 1 ? 'Platz' : 'Plätze'} · Buchung ${b.buchungsnummer}`,
        datum: kurzTag(b.beginn),
        wann: tag(b.beginn),
        beiWahl: ziele.buchung === undefined ? undefined : () => ziele.buchung?.(b.buchungsnummer),
      });
    }
  }

  return schritte.sort((a, b) => a.wann.localeCompare(b.wann));
}

/**
 * DER VERLAUF: was geschehen ist, das Neueste zuerst.
 *
 * Belege mit ihrem Belegdatum und ihrem Stand, vergangene Buchungen am Termin.
 * Höchstens `hoechstens` Einträge — der Rest
 * steht in den Reitern Belege und Buchungen, vollständig.
 */
export function verlaufAus(
  v: Kundenvorgaenge,
  heute: string,
  ziele: Sprungziele,
  hoechstens = 8,
): readonly Verlaufseintrag[] {
  const eintraege: Datiert[] = [];

  for (const a of v.angebote ?? []) {
    eintraege.push({
      kennung: `angebot-${a.id}`,
      art: a.status === 'angenommen' ? 'erledigt' : a.status === 'entwurf' ? 'still' : 'vorgang',
      sinnbild: 'file-text',
      titel: `Angebot ${a.angebotsnummer} · ${ANGEBOTSSTATUS[a.status]}`,
      detail: `${a.betreff} · ${alsEuro(a.belegbrutto)}`,
      datum: kurzTag(a.belegdatum),
      wann: a.belegdatum,
      beiWahl: ziele.angebot === undefined ? undefined : () => ziele.angebot?.(a.id),
    });
  }

  for (const a of v.auftraege ?? []) {
    eintraege.push({
      kennung: `auftrag-${a.id}`,
      art: a.status === 'bestaetigt' ? 'erledigt' : a.status === 'storniert' ? 'still' : 'vorgang',
      sinnbild: 'clipboard-text',
      titel: `Auftrag ${a.auftragsnummer} · ${AUFTRAGSSTATUS[a.status]}`,
      detail: `${a.betreff} · ${alsEuro(a.belegbrutto)}`,
      datum: kurzTag(a.belegdatum),
      wann: a.belegdatum,
      beiWahl: ziele.auftrag === undefined ? undefined : () => ziele.auftrag?.(a.id),
    });
  }

  for (const r of v.rechnungen ?? []) {
    eintraege.push({
      kennung: `rechnung-${r.id}`,
      art:
        r.zahlungsstand === 'bezahlt'
          ? 'erledigt'
          : r.zahlungsstand === 'ueberfaellig'
            ? 'achtung'
            : r.status === 'entwurf'
              ? 'still'
              : 'vorgang',
      sinnbild: 'receipt',
      titel: `Rechnung ${r.rechnungsnummer ?? 'Entwurf'} · ${ZAHLUNGSSTANDNAME[r.zahlungsstand]}`,
      detail: `${r.betreff} · ${alsEuro(r.belegbrutto)}`,
      dringend: r.zahlungsstand === 'ueberfaellig',
      datum: kurzTag(r.belegdatum),
      wann: r.belegdatum,
      beiWahl: ziele.rechnung === undefined ? undefined : () => ziele.rechnung?.(r.id),
    });
  }

  /* RNG-25: gemahnt hat Navision; die Akte zeigt jede Stufe mit Gebühr. */
  for (const m of v.mahnungen ?? []) {
    eintraege.push({
      kennung: `mahnung-${m.rechnung}-${m.mahnstufe}`,
      art: 'achtung',
      sinnbild: 'envelope-simple',
      titel: `${m.mahnstufe}. Mahnung zu ${m.rechnungsnummer ?? 'Rechnung'}`,
      detail:
        Number(m.mahngebuehr) === 0
          ? 'aus Navision, ohne Mahngebühr'
          : `aus Navision · Mahngebühr ${alsEuro(m.mahngebuehr)}`,
      datum: kurzTag(m.mahndatum),
      wann: m.mahndatum,
      beiWahl: ziele.rechnung === undefined ? undefined : () => ziele.rechnung?.(m.rechnung),
    });
  }

  /* SUP-11, KUN-14: jeder E-Mail-Vorgang mit seiner letzten Nachricht. */
  for (const k of v.kommunikation ?? []) {
    eintraege.push({
      kennung: `vorgang-${k.id}`,
      art: k.status === 'neu' ? 'achtung' : k.status === 'geloest' || k.status === 'geschlossen' ? 'erledigt' : 'vorgang',
      sinnbild: 'envelope-simple',
      titel: `E-Mail ${k.nummer} · ${VORGANGSSTATUS[k.status]}`,
      // SUP-09: eine unsichere Zuordnung nennt auch der Verlauf.
      detail: `${k.betreff}${k.anhaenge === 0 ? '' : ` · ${String(k.anhaenge)} ${k.anhaenge === 1 ? 'Anhang' : 'Anhänge'}`}`
        + (k.zuordnung === 'automatisch' && k.sicherheit === 'unsicher' ? ' · unsicher zugeordnet' : ''),
      datum: kurzTag(k.letzteNachrichtAm),
      wann: tag(k.letzteNachrichtAm),
      beiWahl: ziele.vorgang === undefined ? undefined : () => ziele.vorgang?.(k.id, k.postfach),
    });
  }

  for (const b of v.buchungen ?? []) {
    if (tag(b.beginn) < heute) {
      eintraege.push({
        kennung: `buchung-${b.id}`,
        art: b.status === 'storniert' || b.status === 'abgesagt' ? 'still' : 'termin',
        sinnbild: 'ticket',
        titel: `${b.produkt} · ${b.status}`,
        detail: `${b.plaetze} ${b.plaetze === 1 ? 'Platz' : 'Plätze'} · Buchung ${b.buchungsnummer}`,
        datum: kurzTag(b.beginn),
        wann: tag(b.beginn),
        beiWahl: ziele.buchung === undefined ? undefined : () => ziele.buchung?.(b.buchungsnummer),
      });
    }
  }

  return eintraege.sort((a, b) => b.wann.localeCompare(a.wann)).slice(0, hoechstens);
}
